import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    AUTH_MODE,
    isStandalone,
    isPlatform,
    getStoredToken,
    parseJwt,
    clearSession,
} from './authMode';
import NotificationHandler from '../component/NotificationHandler';

const PUBLIC_PATHS = ['/login', '/auth/sso-callback', '/auth/microsoft-callback', '/auth/desk-done', '/set-password'];

/**
 * Replaces legacy ZTool AuthHandler.
 * - standalone: keep user on /login when unauthenticated
 * - platform: redirect to ZSaaS login URL when unauthenticated
 * Legacy ?token=&userDetails= handoff is ignored in platform mode (use /auth/sso-callback).
 */
const AuthGateway = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [status, setStatus] = useState('PENDING');

    useEffect(() => {
        const path = location.pathname;

        // Standalone-only legacy query handoff (deprecated). Prefer login form.
        if (isStandalone()) {
            const searchParams = new URLSearchParams(location.search);
            const tokenFromUrl = searchParams.get('token');
            const userDetailsEncoded = searchParams.get('userDetails');
            if (tokenFromUrl && userDetailsEncoded) {
                try {
                    const userDetails = JSON.parse(decodeURIComponent(userDetailsEncoded));
                    localStorage.setItem('zTaskAuthToken', tokenFromUrl);
                    localStorage.setItem('zTaskUserDetails', JSON.stringify(userDetails));
                    navigate(path, { replace: true, search: '' });
                    setStatus('AUTHENTICATED');
                    return;
                } catch {
                    clearSession();
                }
            }
        }

        if (PUBLIC_PATHS.includes(path)) {
            setStatus('PUBLIC');
            return;
        }

        const token = getStoredToken();
        if (!token) {
            setStatus('FAILED');
            return;
        }

        const decoded = parseJwt(token);
        if (decoded?.exp && decoded.exp * 1000 < Date.now()) {
            clearSession();
            setStatus('EXPIRED');
            return;
        }

        setStatus('AUTHENTICATED');
    }, [location, navigate]);

    useEffect(() => {
        if (status !== 'FAILED' && status !== 'EXPIRED') return;
        if (PUBLIC_PATHS.includes(location.pathname)) return;

        if (isStandalone()) {
            navigate('/login', { replace: true });
            return;
        }

        if (isPlatform()) {
            const loginUrl = process.env.REACT_APP_ZSAAS_LOGIN_URL;
            if (loginUrl && loginUrl.startsWith('http')) {
                const callback =
                    process.env.REACT_APP_ZSAAS_SSO_CALLBACK_URL ||
                    `${window.location.origin}/auth/sso-callback`;
                const target = new URL(loginUrl);
                target.searchParams.set('app', process.env.REACT_APP_ZSAAS_APP_CODE || 'hr');
                target.searchParams.set('redirect', callback);
                window.location.replace(target.toString());
            } else {
                navigate('/login', { replace: true });
            }
        }
    }, [status, location.pathname, navigate]);

    if (status === 'PENDING') {
        return (
            <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
                <h1>Authenticating… ({AUTH_MODE})</h1>
            </div>
        );
    }

    if (status === 'PUBLIC') {
        return children;
    }

    if (status === 'FAILED' || status === 'EXPIRED') {
        return (
            <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
                <h1>{status === 'EXPIRED' ? 'Session expired…' : 'Redirecting to login…'}</h1>
            </div>
        );
    }

    return (
        <>
            <NotificationHandler />
            {children}
        </>
    );
};

export default AuthGateway;
